/**
 * @component LoadingFallback
 * @description Loading screen shown during system initialization
 *
 * ## Component Architecture
 * - **Type**: Presentational
 * - **Parent Context**: App-level loading states, error boundaries
 * - **Child Dependencies**: None - pure UI component
 *
 * ## State Management Strategy
 * @stateType local
 * @stateLibrary useMotionPreference hook from context
 * @stateDescription Animation state controlled by the motion context
 *
 * ## Performance Characteristics
 * @performance React.memo with no props comparison needed
 * @renderFrequency low
 * @optimization React.memo with no props comparison needed
 * @reRenderTriggers Motion context changes (animations enabled/disabled)
 *
 * ## User Interaction Model
 * @primaryActions None - passive loading display
 * @keyboardSupport Not applicable
 * @touchSupport Not applicable
 *
 * ## Accessibility Implementation
 * @accessibility WCAG AA compliant with proper loading announcements
 * @screenReader Announces loading state to screen readers
 * @keyboard No keyboard interaction needed
 * @focusManagement No focus management required
 *
 * @requires react - Component framework
 * @requires @/contexts/MotionContext - Motion preference
 *
 * @exports LoadingFallback - Main component export
 */

import React from 'react';
import { useMotionPreference } from '@/contexts/motionPreference';

/**
 * LoadingFallback component provides the loading screen shown during system
 * initialization.
 *
 * Features animated loading spinner, progress bar, and system initialization text.
 * Respects user's animation preferences from the motion context.
 */
const LoadingFallback: React.FC = React.memo(() => {
  const { animationsEnabled } = useMotionPreference()

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-ui-canvas"
      role="status"
      aria-live="polite"
      aria-label="Loading application"
      data-testid="loading-fallback"
    >
      <div
        className="relative"
        data-testid="loading-container"
      >
        {/* Loading container */}
        <div
          className="relative z-10 flex flex-col items-center space-y-6 p-12"
          data-testid="loading-content"
        >
          {/* Loading spinner */}
          <div
            className="relative w-24 h-24"
            role="progressbar"
            aria-label="Loading progress"
            aria-valuetext="Loading in progress"
            data-testid="loading-spinner"
          >
            {/* Outer ring */}
            <div
              className={`absolute inset-0 border-4 border-ui-now rounded-full ${
                animationsEnabled ? 'animate-spin' : ''
              }`}
              style={{
                borderTopColor: 'transparent',
                borderRightColor: 'transparent',
              }}
              role="presentation"
              aria-hidden="true"
              data-testid="spinner-outer-ring"
            />

            {/* Inner ring */}
            <div
              className={`absolute inset-2 border-4 border-ui-memory rounded-full ${
                animationsEnabled ? 'animate-spin' : ''
              }`}
              style={{
                borderBottomColor: 'transparent',
                borderLeftColor: 'transparent',
                animationDirection: 'reverse',
                animationDuration: '1.5s',
              }}
              role="presentation"
              aria-hidden="true"
              data-testid="spinner-inner-ring"
            />

            {/* Center dot */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              data-testid="spinner-center"
            >
              <div
                className="w-4 h-4 bg-ui-memory rounded-full"
                role="presentation"
                aria-hidden="true"
                data-testid="spinner-center-dot"
              />
            </div>
          </div>

          {/* Loading text */}
          <div
            className="text-center"
            data-testid="loading-text"
          >
            <h2
              className="text-title font-sans text-ui-now mb-2"
              data-testid="loading-title"
            >
              Loading
            </h2>
            <div
              className="flex items-center space-x-1 text-label text-ui-text-muted"
              data-testid="loading-subtitle"
            >
              <span>Initializing system</span>
              <span role="presentation" aria-hidden="true">_</span>
            </div>
          </div>

          {/* Progress bar */}
          <div
            className="w-64 h-1 bg-ui-surface rounded-full overflow-hidden"
            role="progressbar"
            aria-label="System initialization progress"
            aria-valuetext="Progress unknown"
            data-testid="progress-bar"
          >
            <div
              className={`h-full w-full bg-linear-to-r from-ui-now via-ui-memory to-ui-memory ${
                animationsEnabled ? 'loading-progress' : ''
              }`}
              role="presentation"
              aria-hidden="true"
              data-testid="progress-bar-fill"
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes loading-progress {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .loading-progress {
          animation: loading-progress 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
});

LoadingFallback.displayName = 'LoadingFallback';

export default LoadingFallback;
